import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { REQUIREMENT } from "helpers/constants";
import { REQUIREMENT_COMPETENCY_TYPE } from "pages/Requirements/CreateAndEdit/constants";
import {
  useAddEvaluationMetricMutation,
  useAddOrganizationRequirementMutation,
  useAddRequirementCompetenciesMutation,
  useAddRequirementDomainMutation,
  useAddRequirementDomainRequirementsMutation,
  useAddRequirementMutation,
  useAddRequirementTagsMutation,
  useAddTagMutation,
  useGetOrganizationByIdLazyQuery,
  useGetRequirementDomainsLazyQuery,
  useGetRequirementsLazyQuery,
  useGetTagsLazyQuery,
  useUpdateRequirementDomainMutation,
} from "graphql/__generated__/hooks";
import {
  convertRequirementsSpreadsheet,
  CurriculumFrameworkImport,
  FrameworkReviewResult,
  reviewRequirementsSpreadsheet,
} from "services/aiRequirementsImport";
import { milestonesForRequirement } from "./frameworkMilestones";
import {
  buildTagIdByName,
  getOrCreateOrgExclusiveTagId,
} from "pages/UploadCSV/UploadCSVRequirements/orgExclusiveTag";
import { resolveRequirementName } from "pages/UploadCSV/UploadCSVRequirements/parseRequirementsCsv";
import {
  ImportMode,
  ImportReport,
} from "pages/UploadCSV/UploadCSVRequirements/types";

export const FRAMEWORK_STEPS = {
  UPLOAD: "Upload",
  REVIEW: "Review",
  COMPLETE: "Complete",
} as const;

export type FrameworkStep =
  (typeof FRAMEWORK_STEPS)[keyof typeof FRAMEWORK_STEPS];

export const FRAMEWORK_STEP_LABELS: FrameworkStep[] = [
  FRAMEWORK_STEPS.UPLOAD,
  FRAMEWORK_STEPS.REVIEW,
  FRAMEWORK_STEPS.COMPLETE,
];

type Props = {
  mode: ImportMode;
};

export function useImportFrameworkSpreadsheet({ mode }: Props) {
  const { organizationId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<FrameworkStep>(FRAMEWORK_STEPS.UPLOAD);
  const [framework, setFramework] = useState<CurriculumFrameworkImport | null>(
    null,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [aiReview, setAiReview] = useState<FrameworkReviewResult | null>(null);
  const [loadingProgress, setLoadingProgress] = useState({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ImportReport>({
    success: [],
    error: [],
    renamed: [],
    missingLinks: [],
  });

  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();
  const [getRequirements, { data: requirementsData }] =
    useGetRequirementsLazyQuery();
  const [getTags, { data: tagsData }] = useGetTagsLazyQuery();
  const [getRequirementDomains] = useGetRequirementDomainsLazyQuery();

  const [addRequirement] = useAddRequirementMutation();
  const [addOrganizationRequirement] = useAddOrganizationRequirementMutation();
  const [addRequirementTags] = useAddRequirementTagsMutation();
  const [addRequirementCompetencies] = useAddRequirementCompetenciesMutation();
  const [addEvaluationMetric] = useAddEvaluationMetricMutation();
  const [addTag] = useAddTagMutation();
  const [addRequirementDomain] = useAddRequirementDomainMutation();
  const [updateRequirementDomain] = useUpdateRequirementDomainMutation();
  const [addRequirementDomainRequirements] =
    useAddRequirementDomainRequirementsMutation();

  useEffect(() => {
    getRequirements();
    getTags();
    if (mode === "org" && organizationId) {
      getOrganizationById({ variables: { organizationId } });
    }
  }, [mode, organizationId, getRequirements, getTags, getOrganizationById]);

  const organizationName = useMemo(
    () => organizationData?.organization_by_pk?.name || "",
    [organizationData],
  );

  const backRoute = useMemo(() => {
    if (mode === "org" && organizationId) {
      return `/organization/${organizationId}/competencies`;
    }
    return AdminRoutes.CompetenciesList;
  }, [mode, organizationId]);

  const activeLabel = useMemo(
    () => FRAMEWORK_STEP_LABELS.indexOf(step),
    [step],
  );

  const tagIdByName = useMemo(
    () => buildTagIdByName(tagsData?.tag || []),
    [tagsData],
  );

  const importBlocked = useMemo(() => {
    if (!framework) {
      return true;
    }
    if (framework.import_blocked) {
      return true;
    }
    if (aiReview?.issues.some((issue) => issue.severity === "error")) {
      return true;
    }
    return false;
  }, [aiReview, framework]);

  const blockingMessages = useMemo(() => {
    const messages: string[] = [];
    if (framework?.blocking_errors?.length) {
      messages.push(...framework.blocking_errors);
    }
    if (aiReview?.issues) {
      for (const issue of aiReview.issues) {
        if (issue.severity === "error") {
          messages.push(issue.message);
        }
      }
    }
    return messages;
  }, [aiReview, framework]);

  const handleCancel = useCallback(() => {
    navigate(backRoute);
  }, [backRoute, navigate]);

  const handleFinish = useCallback(() => {
    navigate(backRoute);
  }, [backRoute, navigate]);

  const handleFile = useCallback(async (file: File) => {
    setLoading(true);
    setUploadError(null);
    setAiReview(null);
    setReviewError(null);
    setUploadedFile(file);
    try {
      const result = await convertRequirementsSpreadsheet(file);
      if (
        result.domains.length === 0 &&
        result.competencies.length === 0 &&
        result.epas.length === 0
      ) {
        setUploadError("No domains, competencies, or EPAs found in the file.");
        setFramework(null);
        return;
      }
      setFramework(result);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : "Upload failed.");
      setUploadError(message);
      setFramework(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAiReview = useCallback(async () => {
    if (!framework || !uploadedFile) {
      setReviewError("Upload a spreadsheet and wait for conversion first.");
      return;
    }
    setReviewLoading(true);
    setReviewError(null);
    try {
      const result = await reviewRequirementsSpreadsheet(
        uploadedFile,
        framework,
      );
      setAiReview(result);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : "AI review failed.");
      setReviewError(message);
    } finally {
      setReviewLoading(false);
    }
  }, [framework, uploadedFile]);

  const handleApplyAiSuggestions = useCallback(() => {
    if (!aiReview?.suggested_framework) {
      return;
    }
    setFramework(aiReview.suggested_framework);
    setAiReview({
      ...aiReview,
      ok: !aiReview.suggested_framework.import_blocked,
      issues: (aiReview.suggested_framework.blocking_errors || []).map(
        (message) => ({
          severity: "error" as const,
          code: "count_mismatch",
          message,
        }),
      ),
      summary: aiReview.suggested_framework.import_blocked
        ? "Applied AI suggestions, but count checks still fail."
        : "Applied AI suggestions.",
      suggested_framework: undefined,
    });
  }, [aiReview]);

  const onSubmit = useCallback(async () => {
    if (!framework || importBlocked) {
      return;
    }

    setLoading(true);
    const success: string[] = [];
    const errors: string[] = [];
    const renamed: string[] = [];
    const missingLinks: string[] = [];
    const namesInUse = new Set(
      (requirementsData?.requirement || [])
        .map((row) => row.name?.trim().toLowerCase())
        .filter(Boolean) as string[],
    );

    const domainIdByCode = new Map<string, string>();
    const competencyIdByCode = new Map<string, string>();

    const exclusiveTagId =
      mode === "org" && organizationName
        ? await getOrCreateOrgExclusiveTagId({
            organizationName,
            tagIdByName,
            addTag,
            refreshTags: getTags,
          })
        : null;

    const total =
      framework.domains.length +
      framework.competencies.length +
      framework.epas.length;
    let current = 0;
    setLoadingProgress({ current, total });

    const bump = () => {
      current += 1;
      setLoadingProgress({ current, total });
    };

    const orgId = mode === "org" ? organizationId || null : null;

    try {
      const existingDomainsResult = await getRequirementDomains({
        variables: {
          where: orgId
            ? { org_id: { _eq: orgId } }
            : { org_id: { _is_null: true } },
        },
        fetchPolicy: "network-only",
      });
      const existingDomains =
        existingDomainsResult.data?.requirement_domain || [];
      const existingByCode = new Map<string, (typeof existingDomains)[number]>();
      const existingByName = new Map<string, (typeof existingDomains)[number]>();
      for (const row of existingDomains) {
        if (row.code) {
          existingByCode.set(row.code.trim(), row);
        }
        existingByName.set(row.name.trim().toLowerCase(), row);
      }

      for (const domain of framework.domains) {
        try {
          const matched =
            (domain.code
              ? existingByCode.get(domain.code.trim())
              : undefined) ||
            existingByName.get(domain.name.trim().toLowerCase());

          if (matched?.requirement_domain_id) {
            if (!matched.color_hex && domain.color_hex) {
              await updateRequirementDomain({
                variables: {
                  id: matched.requirement_domain_id,
                  name: matched.name,
                  description: matched.description || null,
                  code: matched.code || domain.code || null,
                  sort_order: matched.sort_order || domain.sort_order || null,
                  color_hex: domain.color_hex,
                },
              });
              matched.color_hex = domain.color_hex;
            }
            domainIdByCode.set(domain.code, matched.requirement_domain_id);
            if (matched.code) {
              existingByCode.set(matched.code.trim(), matched);
            }
            existingByName.set(matched.name.trim().toLowerCase(), matched);
            success.push(`Domain (existing): ${matched.name}`);
            bump();
            continue;
          }

          const result = await addRequirementDomain({
            variables: {
              org_id: orgId,
              name: domain.name,
              description: domain.description || null,
              code: domain.code || null,
              sort_order: domain.sort_order || null,
              color_hex: domain.color_hex || null,
            },
          });
          const domainId =
            result.data?.insert_requirement_domain_one?.requirement_domain_id;
          if (domainId) {
            domainIdByCode.set(domain.code, domainId);
            const created = {
              requirement_domain_id: domainId,
              org_id: orgId,
              name: domain.name,
              code: domain.code || null,
              color_hex: domain.color_hex || null,
              sort_order: domain.sort_order || null,
              description: domain.description || null,
            };
            if (domain.code) {
              existingByCode.set(domain.code.trim(), created as any);
            }
            existingByName.set(domain.name.trim().toLowerCase(), created as any);
            success.push(`Domain: ${domain.name}`);
          } else {
            errors.push(`Domain failed: ${domain.name}`);
          }
        } catch (error) {
          errors.push(
            `Domain ${domain.name}: ${
              error instanceof Error ? error.message : "failed"
            }`,
          );
        }
        bump();
      }

      for (const competency of framework.competencies) {
        try {
          const finalName = resolveRequirementName(
            competency.name,
            namesInUse,
            mode === "org" ? organizationName : undefined,
          );
          if (finalName !== competency.name) {
            renamed.push(`${competency.name} → ${finalName}`);
          }

          const overview =
            competency.subcompetencies.length > 0
              ? competency.subcompetencies.map((item, i) => `${i + 1}. ${item}`).join("\n")
              : null;

          const created = await addRequirement({
            variables: {
              name: finalName,
              description: competency.description,
              overview,
              task_time: "0:30",
              animal_size: null,
              type: REQUIREMENT.REQUIREMENT_COMPETENCY,
              requirement_type: REQUIREMENT_COMPETENCY_TYPE.EPA,
              min_performance: 0,
              min_score: 0,
              min_small_animals_count: 0,
              min_large_animals_count: 0,
              species_id: null,
            },
          });
          const requirementId =
            created.data?.insert_requirement?.returning?.[0]?.requirement_id;
          if (!requirementId) {
            errors.push(`Competency failed: ${competency.name}`);
            bump();
            continue;
          }

          namesInUse.add(finalName.toLowerCase());
          competencyIdByCode.set(competency.code, requirementId);

          if (mode === "org" && organizationId) {
            await addOrganizationRequirement({
              variables: {
                org_id: organizationId,
                requirement_id: requirementId,
              },
            });
          }

          if (exclusiveTagId) {
            await addRequirementTags({
              variables: {
                objects: [
                  { requirement_id: requirementId, tag_id: exclusiveTagId },
                ],
              },
            });
          }

          const competencyMilestones = milestonesForRequirement(
            framework.milestones,
            "competency",
            competency.code,
          );
          if (competencyMilestones.length > 0) {
            await addEvaluationMetric({
              variables: {
                objects: competencyMilestones.map((milestone) => ({
                  requirement_id: requirementId,
                  skill_level: milestone.skill_level,
                  target_achievement_time: milestone.skill_level,
                  description: milestone.description || milestone.name,
                  skill_level_label: milestone.name,
                  target_achievement_time_label: milestone.name,
                })),
              },
            });
          }

          const domainId = domainIdByCode.get(competency.domain_code);
          if (domainId) {
            await addRequirementDomainRequirements({
              variables: {
                objects: [
                  {
                    requirement_domain_id: domainId,
                    requirement_id: requirementId,
                    relation: "member",
                  },
                ],
              },
            });
          } else if (competency.domain_code) {
            missingLinks.push(
              `Competency ${competency.code}: domain ${competency.domain_code} not found`,
            );
          }

          success.push(`Competency: ${finalName}`);
        } catch (error) {
          errors.push(
            `Competency ${competency.name}: ${
              error instanceof Error ? error.message : "failed"
            }`,
          );
        }
        bump();
      }

      for (const epa of framework.epas) {
        try {
          const finalName = resolveRequirementName(
            epa.name,
            namesInUse,
            mode === "org" ? organizationName : undefined,
          );
          if (finalName !== epa.name) {
            renamed.push(`${epa.name} → ${finalName}`);
          }

          const created = await addRequirement({
            variables: {
              name: finalName,
              description: epa.description,
              overview: epa.commentary || null,
              task_time: "0:30",
              animal_size: null,
              type: REQUIREMENT.REQUIREMENT_EPA,
              requirement_type: "EPA",
              min_performance: 0,
              min_score: 0,
              min_small_animals_count: 0,
              min_large_animals_count: 0,
              species_id: null,
            },
          });
          const requirementId =
            created.data?.insert_requirement?.returning?.[0]?.requirement_id;
          if (!requirementId) {
            errors.push(`EPA failed: ${epa.name}`);
            bump();
            continue;
          }

          namesInUse.add(finalName.toLowerCase());

          if (mode === "org" && organizationId) {
            await addOrganizationRequirement({
              variables: {
                org_id: organizationId,
                requirement_id: requirementId,
              },
            });
          }

          if (exclusiveTagId) {
            await addRequirementTags({
              variables: {
                objects: [
                  { requirement_id: requirementId, tag_id: exclusiveTagId },
                ],
              },
            });
          }

          const epaMilestones = milestonesForRequirement(
            framework.milestones,
            "epa",
            epa.code,
          );
          if (epaMilestones.length > 0) {
            await addEvaluationMetric({
              variables: {
                objects: epaMilestones.map((milestone) => ({
                  requirement_id: requirementId,
                  skill_level: milestone.skill_level,
                  target_achievement_time: milestone.skill_level,
                  description: milestone.description || milestone.name,
                  skill_level_label: milestone.name,
                  target_achievement_time_label: milestone.name,
                })),
              },
            });
          }

          const competencyLinks = epa.competency_codes
            .map((code) => competencyIdByCode.get(code))
            .filter((id): id is string => Boolean(id))
            .map((competency_id) => ({
              requirement_id: requirementId,
              competency_id,
            }));

          for (const code of epa.competency_codes) {
            if (!competencyIdByCode.has(code)) {
              missingLinks.push(`EPA ${epa.code}: competency ${code} missing`);
            }
          }

          if (competencyLinks.length > 0) {
            await addRequirementCompetencies({
              variables: { objects: competencyLinks },
            });
          }

          const domainLinks: Array<{
            requirement_domain_id: string;
            requirement_id: string;
            relation: string;
          }> = [];
          const linkedDomainIds = new Set<string>();
          for (const code of epa.primary_domain_codes) {
            const domainId = domainIdByCode.get(code);
            if (domainId) {
              domainLinks.push({
                requirement_domain_id: domainId,
                requirement_id: requirementId,
                relation: "primary",
              });
              linkedDomainIds.add(domainId);
            }
          }
          for (const code of epa.secondary_domain_codes) {
            const domainId = domainIdByCode.get(code);
            if (domainId && !linkedDomainIds.has(domainId)) {
              domainLinks.push({
                requirement_domain_id: domainId,
                requirement_id: requirementId,
                relation: "secondary",
              });
              linkedDomainIds.add(domainId);
            }
          }
          // Ensure EPA shows every domain represented by its competencies.
          for (const competencyCode of epa.competency_codes) {
            const competency = framework.competencies.find(
              (row) => row.code === competencyCode,
            );
            const domainCode = competency?.domain_code;
            if (!domainCode) {
              continue;
            }
            const domainId = domainIdByCode.get(domainCode);
            if (domainId && !linkedDomainIds.has(domainId)) {
              domainLinks.push({
                requirement_domain_id: domainId,
                requirement_id: requirementId,
                relation: "secondary",
              });
              linkedDomainIds.add(domainId);
            }
          }
          if (domainLinks.length > 0) {
            await addRequirementDomainRequirements({
              variables: { objects: domainLinks },
            });
          }

          success.push(`EPA: ${finalName}`);
        } catch (error) {
          errors.push(
            `EPA ${epa.name}: ${
              error instanceof Error ? error.message : "failed"
            }`,
          );
        }
        bump();
      }
    } finally {
      setReport({
        success,
        error: errors,
        renamed,
        missingLinks,
      });
      setLoading(false);
      setStep(FRAMEWORK_STEPS.COMPLETE);
    }
  }, [
    addEvaluationMetric,
    addOrganizationRequirement,
    addRequirement,
    addRequirementCompetencies,
    addRequirementDomain,
    addRequirementDomainRequirements,
    addRequirementTags,
    addTag,
    framework,
    getRequirementDomains,
    getTags,
    importBlocked,
    mode,
    organizationId,
    organizationName,
    requirementsData,
    tagIdByName,
    updateRequirementDomain,
  ]);

  return {
    step,
    activeLabel,
    framework,
    uploadError,
    loading,
    reviewLoading,
    reviewError,
    aiReview,
    importBlocked,
    blockingMessages,
    loadingProgress,
    report,
    organizationName,
    setStep,
    handleFile,
    handleAiReview,
    handleApplyAiSuggestions,
    onSubmit,
    handleFinish,
    handleCancel,
  };
}
